import { messages as friendInteractionMessages } from "common/FriendInteractionsButtons";
import { ConfirmationDialog } from "common/styleguide/ConfirmationDialog";
import fetchJSON from "lib/fetchJSON";
import { commonCTAs, useFormatMessage } from "lib/localization";
import { errorMessage } from "lib/styleguide/base.scss";
import { c } from "lib/textUtils";
import { getCurrentUser } from "lib/utils";
import React, { useState } from "react";
import { defineMessages } from "react-intl";
import styles from "./ConfirmUpdateFollowModal.scss";

export const CONFIRM_UPDATE_FOLLOW_MODAL = "CONFIRM_UPDATE_FOLLOW_MODAL";

const messages = defineMessages({
  blockTitle: {
    id: "ConfirmUpdateFollowModal.blockTitle",
    defaultMessage: "Block User",
  },
  blockLabel: {
    id: "ConfirmUpdateFollowModal.blockLabel",
    defaultMessage:
      "Blocking this user will prevent them from seeing any of your future activities. They will not be notified that you have blocked them. Note: This action is not reversible.",
  },
  spamTitle: {
    id: "ConfirmUpdateFollowModal.spamTitle",
    defaultMessage: "Report spam",
  },
  spamLabel: {
    id: "ConfirmUpdateFollowModal.spamLabel",
    defaultMessage: "Are you sure you want to report {user} for spam?",
  },
  harassmentTitle: {
    id: "ConfirmUpdateFollowModal.harassmentTitle",
    defaultMessage: "Report harassment",
  },
  harassmentLabel: {
    id: "ConfirmUpdateFollowModal.harassmentLabel",
    defaultMessage:
      "Harassment is not tolerated by Ride with GPS. After submitting your report, this user will immediately be blocked from seeing any of your future activities and our Support Team will follow up with you to discuss next steps.",
  },
  unfollowLabel: {
    id: "ConfirmUpdateFollowModal.unfollowLabel",
    defaultMessage:
      "You will no longer see updates on this user’s activity in your feed. They will not be notified that you have unfollowed them and you can re-follow them at any time. ",
  },
  successTitle: {
    id: "ConfirmUpdateFollowModal.successTitle",
    defaultMessage: "Report Submitted",
  },
  successLabel: {
    id: "ConfirmUpdateFollowModal.successLabel",
    defaultMessage: "Your report has been successfully submitted. Our Support Team will be in touch via email shortly.",
  },
});

const requester = (currentUser) => {
  const cu = getCurrentUser();
  return {
    name: `${cu.name} (ID ${cu.id})`,
    email: currentUser.email,
  };
};

export const ConfirmUpdateFollowModal = ({ currentUser, reportedUser, targetLevel, onConfirm, onRequestClose }) => {
  const fm = useFormatMessage();
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [comment, setComment] = useState(null);
  const [error, setError] = useState(false);

  const submit = (e) => {
    if (!["spam", "harassment"].includes(targetLevel)) {
      onRequestClose();
      onConfirm();
      return;
    }
    if (sending) return;
    setSending(true);

    if (targetLevel === "harassment" && !comment) {
      setError(true);
      setSending(false);
      return;
    }
    const subject = `${c(targetLevel)} report of user ${reportedUser.id} by user ${currentUser.id}`;
    const toPad = Math.ceil(50 - subject.length, 0);
    const bodyComment = `${subject}${" ".repeat(toPad)} \n\n------------------\n\n ${comment}`;

    const body = {
      request: {
        requester: requester(currentUser),
        comment: bodyComment,
        location: window.location.href,
      },
    };

    fetchJSON("/create_ticket.json", body, { method: "POST" }).then((r) => {
      setSending(false);
      setSuccess(true);
      onConfirm();
    });
  };

  if (success && targetLevel === "harassment") {
    return <Success onRequestClose={onRequestClose} />;
  }

  let title, label;
  switch (targetLevel) {
    case "block":
      title = fm(messages.blockTitle);
      label = fm(messages.blockLabel);
      break;
    case "spam":
      title = fm(messages.spamTitle);
      label = fm(messages.spamLabel, { user: reportedUser.name });
      break;
    case "harassment":
      title = fm(messages.harassmentTitle);
      label = fm(messages.harassmentLabel);
      break;
    case "unfollow":
      title = fm(friendInteractionMessages.unfollow);
      label = fm(messages.unfollowLabel);
      break;
  }

  return (
    <ConfirmationDialog
      onRequestClose={onRequestClose}
      onCancel={onRequestClose}
      cancelText={fm(commonCTAs.cancel)}
      onConfirm={submit}
      confirmText={fm(commonCTAs.confirm)}
      maxWidth="600px"
      title={title}
    >
      {targetLevel === "harassment" ? (
        <form className={styles.form}>
          <label> {label}</label>
          <textarea
            rows={5}
            defaultValue={comment}
            onChange={(e) => setComment(e.target.value)}
            className={error ? errorMessage : undefined}
            required
          />
        </form>
      ) : (
        <label> {label}</label>
      )}
    </ConfirmationDialog>
  );
};

const Success = ({ onRequestClose }) => {
  const fm = useFormatMessage();
  return (
    <ConfirmationDialog
      showCancel={false}
      title={fm(messages.successTitle)}
      onRequestClose={onRequestClose}
      onConfirm={onRequestClose}
      confirmText={c(fm(commonCTAs.ok))}
      maxWidth="600px"
    >
      <div className={styles.success}>{fm(messages.successLabel)}</div>
    </ConfirmationDialog>
  );
};
